import { Theme } from '../models/theme';
import styles from './ImageIcon.module.scss';
import spinnerImage from '@assets/images/spinner.svg';
import spinnerLightThemeImage from '@assets/images/light-theme/spinner.svg';
import trafficSituationSlightImage from '@assets/images/traffic-situation-slight.svg';
import trafficSituationSlightLightThemeImage from '@assets/images/light-theme/traffic-situation-slight.svg';
import trafficSituationNormalImage from '@assets/images/traffic-situation-normal.svg';
import trafficSituationNormalLightThemeImage from '@assets/images/light-theme/traffic-situation-normal.svg';
import trafficSituationSevereImage from '@assets/images/traffic-situation-severe.svg';
import trafficSituationSevereLightThemeImage from '@assets/images/light-theme/traffic-situation-severe.svg';
import wheelcharAccessibleImage from '@assets/images/wheelchair-accessible.svg';
import wheelcharAccessibleLightThemeImage from '@assets/images/light-theme/wheelchair-accessible.svg';
import campaignImage from '@assets/images/campaign.svg';
import campaignLightThemeImage from '@assets/images/light-theme/campaign.svg';
import transportModeTramImage from '@assets/images/transport-mode-tram.svg';
import transportModeTramLightThemeImage from '@assets/images/light-theme/transport-mode-tram.svg';
import transportModeBusImage from '@assets/images/transport-mode-bus.svg';
import transportModeBusLightThemeImage from '@assets/images/light-theme/transport-mode-bus.svg';
import transportModeFerryImage from '@assets/images/transport-mode-ferry.svg';
import transportModeFerryLightThemeImage from '@assets/images/light-theme/transport-mode-ferry.svg';
import transportModeTrainImage from '@assets/images/transport-mode-train.svg';
import transportModeTrainLightThemeImage from '@assets/images/light-theme/transport-mode-train.svg';
import noImage from '@assets/images/no.svg';
import logoImage from '@assets/images/logo.svg';
import playImage from '@assets/images/play.svg';
import playLightThemeImage from '@assets/images/light-theme/play.svg';
import pauseImage from '@assets/images/pause.svg';
import pauseLightThemeImage from '@assets/images/light-theme/pause.svg';
import { forwardRef, Ref, useContext } from 'react';
import { ThemeContext } from '@shared/contexts/ThemeContext';

export type Icon =
  | 'spinner'
  | 'traffic-situation-slight'
  | 'traffic-situation-normal'
  | 'traffic-situation-severe'
  | 'wheelchair-accessible'
  | 'campaign'
  | 'transport-mode-tram'
  | 'transport-mode-bus'
  | 'transport-mode-ferry'
  | 'transport-mode-train'
  | 'no'
  | 'logo'
  | 'play'
  | 'pause';

interface ImageIconProps {
  type: Icon;
  alt: string;
  className?: string;
  themeOverride?: Theme;
}

const ImageIcon = forwardRef(
  (
    { type, alt, className, themeOverride }: ImageIconProps,
    ref: Ref<HTMLImageElement>,
  ) => {
    const theme = useContext(ThemeContext);

    function getImageSource(theme: Theme): string {
      switch (type) {
        case 'spinner':
          return theme === 'light' ? spinnerLightThemeImage : spinnerImage;
        case 'traffic-situation-slight':
          return theme === 'light'
            ? trafficSituationSlightLightThemeImage
            : trafficSituationSlightImage;
        case 'traffic-situation-normal':
          return theme === 'light'
            ? trafficSituationNormalLightThemeImage
            : trafficSituationNormalImage;
        case 'traffic-situation-severe':
          return theme === 'light'
            ? trafficSituationSevereLightThemeImage
            : trafficSituationSevereImage;
        case 'wheelchair-accessible':
          return theme === 'light'
            ? wheelcharAccessibleLightThemeImage
            : wheelcharAccessibleImage;
        case 'campaign':
          return theme === 'light' ? campaignLightThemeImage : campaignImage;
        case 'transport-mode-tram':
          return theme === 'light'
            ? transportModeTramLightThemeImage
            : transportModeTramImage;
        case 'transport-mode-bus':
          return theme === 'light'
            ? transportModeBusLightThemeImage
            : transportModeBusImage;
        case 'transport-mode-ferry':
          return theme === 'light'
            ? transportModeFerryLightThemeImage
            : transportModeFerryImage;
        case 'transport-mode-train':
          return theme === 'light'
            ? transportModeTrainLightThemeImage
            : transportModeTrainImage;
        case 'no':
          return noImage;
        case 'logo':
          return logoImage;
        case 'play':
          return theme === 'light' ? playLightThemeImage : playImage;
        case 'pause':
          return theme === 'light' ? pauseLightThemeImage : pauseImage;
      }
    }

    return (
      <img
        ref={ref}
        className={`${styles.icon}${
          className !== undefined ? ' ' + className : ''
        }`}
        src={getImageSource(themeOverride ?? theme)}
        title={alt}
        alt={alt}
      />
    );
  },
);

ImageIcon.displayName = 'ImageIcon';

export default ImageIcon;
